import { getAskAISuggestion } from "../../api";
import { formatAmount } from "../../HelperFunctions/PayrollEntryHelpers/payrollFormatters";

export const fetchAISuggestions = async (payload) => {
    const response = await getAskAISuggestion(payload);

    const dataList =
        response?.ASKAISuggestion ||
        (Array.isArray(response) ? response : []);

    const allowedSources = [4, 5, 6, 7];
    const autoMapSources = [1, 2, 3];

    const manualItems = dataList.filter(item =>
        allowedSources.includes(Number(item.ClassificationSource))
    );

    const autoMapItems = dataList.filter(item =>
        autoMapSources.includes(Number(item.ClassificationSource))
    );

    return {
        dataList,
        manualItems,
        autoMapItems
    };
};

export const applyAISuggestions = ({
    suggestions = [],
    wages = [],
    addDeds = [],
    taxes = [],
    displayHours = (h) => h,
    isHoursDecimal = false,
    overwriteExisting = false,
    clearCOA = false,
    payrollItems = []
}) => {
    // Empty all COA dropdown values only if clearCOA is explicitly true
    const newWages = clearCOA ? wages.map(w => ({ ...w, coa: "", coaName: "" })) : [...wages];
    const newAddDeds = clearCOA ? addDeds.map(a => ({ ...a, coa: "", coaName: "" })) : [...addDeds];
    const newTaxes = clearCOA ? taxes.map(t => ({ ...t, coa: "", coaName: "", expenseCoa: "" })) : [...taxes];

    const isBlankWage = (w) => !w.wageName && !w.mappedTo && !w.coa && (!w.gross || Number(w.gross) === 0);
    const isBlankAddDed = (a) => !a.item && !a.mappedTo && !a.coa && (!a.amount || Number(a.amount) === 0);
    const isBlankTax = (t) => !t.item && !t.mappedTo && !t.coa && (!t.amount || Number(t.amount) === 0);

    const usedWageIndices = new Set();
    const usedAddDedIndices = new Set();
    const usedTaxIndices = new Set();

    suggestions.forEach(sugg => {
        const type = Number(sugg.ItemType);
        const importedName = String(sugg.ImportedItemName || "").trim().toLowerCase();
        const itemName = String(sugg.ItemName || "").trim().toLowerCase();
        const targetId = sugg.ItemID || sugg.TargetID || (sugg.PayrollItemID ? String(sugg.PayrollItemID) : "");
        const rawAccId = sugg.AccountID !== undefined && sugg.AccountID !== null ? sugg.AccountID : (sugg.COA || sugg.AccountId || "");
        const accountId = String(rawAccId || "").trim();
        const rawAccName = sugg.AccountName || sugg.COAName || sugg.Account || "";
        const accountName = rawAccName && rawAccName !== "." ? String(rawAccName).trim() : "";
        const liabilityAccountId = sugg.LiabilityAccountID !== undefined && sugg.LiabilityAccountID !== null ? String(sugg.LiabilityAccountID).trim() : (sugg.ExpenseAccountID || sugg.ExpenseCOA || "");

        // 1. WAGES (type === 8 || type === 0 || TypeClassification === "Wages")
        if (sugg.TypeClassification === "Wages" || type === 8 || type === 0) {
            const isWageClass7 = Number(sugg.ClassificationSource) === 7;
            let rowIdx = -1;

            if (importedName) {
                rowIdx = newWages.findIndex((w, i) => !usedWageIndices.has(i) && String(w.wageName || "").trim().toLowerCase() === importedName);
            }

            if (rowIdx === -1 && itemName) {
                rowIdx = newWages.findIndex((w, i) => !usedWageIndices.has(i) && (
                    String(w.wageName || "").trim().toLowerCase() === itemName ||
                    String(w.mappedToName || "").trim().toLowerCase() === itemName
                ));
            }

            if (rowIdx === -1 && targetId) {
                rowIdx = newWages.findIndex((w, i) => !usedWageIndices.has(i) && String(w.mappedTo).toLowerCase() === String(targetId).toLowerCase());
            }

            if (rowIdx === -1) {
                rowIdx = newWages.findIndex((w, i) => !usedWageIndices.has(i) && isBlankWage(w));
            }

            if (rowIdx > -1) {
                usedWageIndices.add(rowIdx);
                if (!overwriteExisting && newWages[rowIdx].mappedTo && newWages[rowIdx].coa) return;

                let finalMappedTo = targetId || newWages[rowIdx].mappedTo || "";
                const finalMappedToName = sugg.ItemName || newWages[rowIdx].mappedToName || "";

                let matchedPayrollItem = null;
                if (Array.isArray(payrollItems) && payrollItems.length > 0) {
                    if (!finalMappedTo && finalMappedToName) {
                        matchedPayrollItem = payrollItems.find(item =>
                            (Number(item.ItemType) === 8 || Number(item.ItemType) === 0) &&
                            item.PayrollItemName?.trim().toLowerCase() === finalMappedToName.trim().toLowerCase()
                        );
                        if (matchedPayrollItem?.ItemID) finalMappedTo = matchedPayrollItem.ItemID;
                    } else if (finalMappedTo) {
                        matchedPayrollItem = payrollItems.find(item =>
                            String(item.ItemID || item.ItemId || item.Id || "").toLowerCase() === String(finalMappedTo).toLowerCase()
                        );
                    }
                }

                const fallbackAccId = matchedPayrollItem?.AccountID || matchedPayrollItem?.COA || "";
                const fallbackAccName = matchedPayrollItem?.AccountName || matchedPayrollItem?.COAName || "";

                newWages[rowIdx] = {
                    ...newWages[rowIdx],
                    wageName: newWages[rowIdx].wageName || sugg.ImportedItemName || sugg.ItemName || "",
                    mappedTo: finalMappedTo,
                    mappedToName: finalMappedToName,
                    coa: isWageClass7 ? "" : (accountId || fallbackAccId || newWages[rowIdx].coa || ""),
                    coaName: isWageClass7 ? "" : (accountName || fallbackAccName || newWages[rowIdx].coaName || ""),
                    id: sugg.PayrollDetailID || newWages[rowIdx].id || Date.now() + Math.random(),
                    hrs: sugg.Hrs !== undefined && sugg.Hrs !== null ? (isHoursDecimal ? formatAmount(sugg.Hrs) : displayHours(sugg.Hrs)) : newWages[rowIdx].hrs,
                    rate: sugg.Rate !== undefined && sugg.Rate !== null ? formatAmount(sugg.Rate) : newWages[rowIdx].rate,
                    gross: sugg.Amount !== undefined && sugg.Amount !== null ? formatAmount(sugg.Amount) : newWages[rowIdx].gross
                };
            }
        }

        // 2. ADDITIONS / DEDUCTIONS (type === 9 || type === 10 || TypeClassification === "Additions" || "Deductions")
        else if (type === 9 || type === 10 || sugg.TypeClassification === "Additions" || sugg.TypeClassification === "Deductions") {
            let rowIdx = -1;

            if (importedName) {
                rowIdx = newAddDeds.findIndex((a, i) => !usedAddDedIndices.has(i) && String(a.item || "").trim().toLowerCase() === importedName && (Number(a.addDeduction) === type || !a.addDeduction || !type));
            }

            if (rowIdx === -1 && itemName) {
                rowIdx = newAddDeds.findIndex((a, i) => !usedAddDedIndices.has(i) && (
                    String(a.item || "").trim().toLowerCase() === itemName ||
                    String(a.mappedToName || "").trim().toLowerCase() === itemName
                ) && (Number(a.addDeduction) === type || !a.addDeduction || !type));
            }

            if (rowIdx === -1 && targetId) {
                rowIdx = newAddDeds.findIndex((a, i) => !usedAddDedIndices.has(i) && String(a.mappedTo).toLowerCase() === String(targetId).toLowerCase());
            }

            if (rowIdx === -1) {
                rowIdx = newAddDeds.findIndex((a, i) => !usedAddDedIndices.has(i) && isBlankAddDed(a));
            }

            if (rowIdx > -1) {
                usedAddDedIndices.add(rowIdx);
                if (!overwriteExisting && newAddDeds[rowIdx].mappedTo && newAddDeds[rowIdx].coa) return;

                let finalMappedTo = targetId || newAddDeds[rowIdx].mappedTo || "";
                const finalMappedToName = sugg.ItemName || newAddDeds[rowIdx].mappedToName || "";

                let matchedPayrollItem = null;
                if (Array.isArray(payrollItems) && payrollItems.length > 0) {
                    if (!finalMappedTo && finalMappedToName) {
                        matchedPayrollItem = payrollItems.find(item =>
                            Number(item.ItemType) === type &&
                            item.PayrollItemName?.trim().toLowerCase() === finalMappedToName.trim().toLowerCase()
                        );
                        if (matchedPayrollItem?.ItemID) finalMappedTo = matchedPayrollItem.ItemID;
                    } else if (finalMappedTo) {
                        matchedPayrollItem = payrollItems.find(item =>
                            String(item.ItemID || item.ItemId || item.Id || "").toLowerCase() === String(finalMappedTo).toLowerCase()
                        );
                    }
                }

                const fallbackAccId = matchedPayrollItem?.AccountID || matchedPayrollItem?.COA || "";
                const fallbackAccName = matchedPayrollItem?.AccountName || matchedPayrollItem?.COAName || "";

                newAddDeds[rowIdx] = {
                    ...newAddDeds[rowIdx],
                    addDeduction: newAddDeds[rowIdx].addDeduction || type,
                    item: newAddDeds[rowIdx].item || sugg.ImportedItemName || sugg.ItemName || "",
                    mappedTo: finalMappedTo,
                    mappedToName: finalMappedToName,
                    coa: accountId || fallbackAccId || newAddDeds[rowIdx].coa || "",
                    coaName: accountName || fallbackAccName || newAddDeds[rowIdx].coaName || "",
                    id: sugg.PayrollDetailID || newAddDeds[rowIdx].id || Date.now() + Math.random(),
                    amount: sugg.Amount !== undefined && sugg.Amount !== null ? formatAmount(Math.abs(sugg.Amount)) : newAddDeds[rowIdx].amount
                };
            }
        }

        // 3. TAXES (type === 11 || type === 12 || TypeClassification === "Taxes")
        else if (type === 11 || type === 12 || sugg.TypeClassification === "Taxes" || sugg.TypeClassification === "EmployeeTaxes" || sugg.TypeClassification === "EmployerTaxes") {
            let rowIdx = -1;

            if (importedName) {
                rowIdx = newTaxes.findIndex((t, i) => !usedTaxIndices.has(i) && String(t.item || "").trim().toLowerCase() === importedName && (Number(t.taxType) === type || !t.taxType || !type));
            }

            if (rowIdx === -1 && itemName) {
                rowIdx = newTaxes.findIndex((t, i) => !usedTaxIndices.has(i) && (
                    String(t.item || "").trim().toLowerCase() === itemName ||
                    String(t.mappedToName || "").trim().toLowerCase() === itemName
                ) && (Number(t.taxType) === type || !t.taxType || !type));
            }

            if (rowIdx === -1 && targetId) {
                rowIdx = newTaxes.findIndex((t, i) => !usedTaxIndices.has(i) && String(t.mappedTo).toLowerCase() === String(targetId).toLowerCase());
            }

            if (rowIdx === -1) {
                rowIdx = newTaxes.findIndex((t, i) => !usedTaxIndices.has(i) && isBlankTax(t));
            }

            if (rowIdx > -1) {
                usedTaxIndices.add(rowIdx);
                if (!overwriteExisting && newTaxes[rowIdx].mappedTo && newTaxes[rowIdx].coa) return;

                let finalMappedTo = targetId || newTaxes[rowIdx].mappedTo || "";
                const finalMappedToName = sugg.ItemName || newTaxes[rowIdx].mappedToName || "";

                let matchedPayrollItem = null;
                if (Array.isArray(payrollItems) && payrollItems.length > 0) {
                    if (!finalMappedTo && finalMappedToName) {
                        matchedPayrollItem = payrollItems.find(item =>
                            Number(item.ItemType) === type &&
                            item.PayrollItemName?.trim().toLowerCase() === finalMappedToName.trim().toLowerCase()
                        );
                        if (matchedPayrollItem?.ItemID) finalMappedTo = matchedPayrollItem.ItemID;
                    } else if (finalMappedTo) {
                        matchedPayrollItem = payrollItems.find(item =>
                            String(item.ItemID || item.ItemId || item.Id || "").toLowerCase() === String(finalMappedTo).toLowerCase()
                        );
                    }
                }

                const fallbackAccId = matchedPayrollItem?.AccountID || matchedPayrollItem?.COA || "";
                const fallbackAccName = matchedPayrollItem?.AccountName || matchedPayrollItem?.COAName || "";

                newTaxes[rowIdx] = {
                    ...newTaxes[rowIdx],
                    taxType: newTaxes[rowIdx].taxType || type,
                    item: newTaxes[rowIdx].item || sugg.ImportedItemName || sugg.ItemName || "",
                    mappedTo: finalMappedTo,
                    mappedToName: finalMappedToName,
                    coa: accountId || fallbackAccId || newTaxes[rowIdx].coa || "",
                    coaName: accountName || fallbackAccName || newTaxes[rowIdx].coaName || "",
                    expenseCoa: type === 12 ? (liabilityAccountId || newTaxes[rowIdx].expenseCoa || "") : "",
                    id: sugg.PayrollDetailID || newTaxes[rowIdx].id || Date.now() + Math.random(),
                    amount: sugg.Amount !== undefined && sugg.Amount !== null ? formatAmount(Math.abs(sugg.Amount)) : newTaxes[rowIdx].amount
                };
            }
        }
    });

    return {
        wages: newWages,
        addDeds: newAddDeds,
        taxes: newTaxes
    };
};